// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SideNavHeading.tsx
 * @input Uses React, useRef, useCallback, ReactNode, StyleX, usePopover
 * @output Exports SideNavHeading component and SideNavHeadingProps
 * @position Core implementation; used inside SideNav header slot
 *
 * Product/suite/account heading with smart interaction boundary logic.
 * Composes usePopover internally when menu prop is provided.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/SideNav/SideNav.doc.mjs
 * - /packages/core/src/SideNav/SideNav.test.tsx
 * - /packages/core/src/SideNav/index.ts
 * - /apps/storybook/stories/SideNav.stories.tsx
 * - /packages/cli/templates/blocks/components/SideNav/ (showcase blocks)
 */
import { useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, fontWeightVars, radiusVars, typeScaleVars } from "../theme/tokens.stylex.js";
// TODO(#264): Lazy-load usePopover so the popover/layer bundle is not
// eagerly imported when `menu` is not provided. See Text for an example
// of lazy loading layer resources.
import { usePopover } from "../Popover/usePopover.js";
import { Link } from "../Link/index.js";
import { getIcon } from "../Icon/globalIconRegistry.js";
import { Tooltip } from "../Tooltip/index.js";
import { navItemStyles } from "../NavItem/navItemStyles.stylex.js";
import { useSideNavCollapse } from "./SideNavCollapseContext.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useMenuHover } from "../hooks/useMenuHover.js";
import { NavHeadingCloseContext } from "../NavMenu/NavMenuContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kAzted: "xkoleio",
    kZCmMZ: "x12gdq22 x1betex2",
    kwRFfy: "x1djylfy",
    k8WAf4: "xt970qd",
    kB7OPa: "x9f619",
    kybGjl: "x1hl2dhg",
    kMwMTN: "x1heor9g",
    kkrTdU: "xt0e3qv",
    $$css: true
  },
  rootCollapsed: {
    kjj79g: "xl56j7k",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  menuTrigger: {
    kkrTdU: "x1ypdohk",
    kaIpWk: "xh6dtrn",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1qlqyl8",
    k63SB2: "x1sodnla",
    k9WMMc: "x1yc453h",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    keoZOQ: "xcsaf9d",
    $$css: true
  },
  popoverOverlap: {
    k7Eaqz: "x8wyhu6",
    keoZOQ: "xvyqdj1",
    keTefX: "x1qfufaz",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * Product/suite/account heading for SideNav.
 *
 * Supports smart interaction boundary logic:
 * - No hrefs + menu → whole heading is the popover trigger
 * - headingHref only, no menu → whole heading is one link
 * - headingHref + superheadingHref, no menu → each is an independent link
 * - menu + hrefs → links are independent, chevron/remaining area is the trigger
 *
 * The chevron indicator is automatically shown when `menu` is provided.
 *
 * @example
 * ```
 * <SideNavHeading icon={<AppIcon />} heading="My App" headingHref="/" />
 * <SideNavHeading
 *   icon={<SuiteIcon />}
 *   superheading="Suite Name"
 *   superheadingHref="/suite"
 *   heading="Product Name"
 *   headingHref="/product"
 *   menu={<ProductSwitcher />}
 * />
 * <SideNavHeading
 *   icon={<AppIcon />}
 *   heading="Product Name"
 *   subheading="Business Account"
 *   menu={<AccountSwitcher />}
 * />
 * ```
 */
export function SideNavHeading({
  as,
  icon,
  heading,
  headingHref,
  superheading,
  superheadingHref,
  subheading,
  subheadingHref,
  headerEndContent,
  menu,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref,
  ...props
}) {
  const t = useTranslator();
  const LinkComponent = useLinkComponent(as);
  const {
    isCollapsed
  } = useSideNavCollapse();
  const rootRef = useRef(null);
  const collapsedItemRef = useRef(null);
  const popover = usePopover({
    dialogLabel: t('@astryx.sideNav.heading.dialogLabel'),
    hasCloseButton: false
  });
  const closeMenuCtx = useMemo(() => ({
    closeMenu: popover.hide
  }), [popover.hide]);
  const {
    triggerProps,
    contentProps,
    menuRef,
    setTriggerEl
  } = useMenuHover({
    show: popover.show,
    hide: popover.hide,
    isOpen: popover.isOpen,
    isEnabled: !!menu,
    showDelay: 0
  });
  const setRef = mergeRefs(rootRef, setTriggerEl, ref, menu ? popover.triggerRef : undefined);

  // In collapsed mode: hide if no icon, show icon-only if has icon
  if (isCollapsed && !icon) {
    return null;
  }
  if (isCollapsed && icon) {
    const collapsedIcon = /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
      },
      children: icon
    });
    const collapsedSetRef = mergeRefs(collapsedItemRef, ref);
    let collapsedElement;
    if (headingHref) {
      collapsedElement = /*#__PURE__*/_jsx(LinkComponent, {
        ref: collapsedSetRef,
        href: headingHref,
        "aria-label": heading,
        "data-testid": testId,
        ...mergeProps(themeProps('side-nav-heading'), stylex.props(navItemStyles.item, styles.rootCollapsed, xstyle), className, style),
        children: collapsedIcon
      });
    } else if (menu) {
      collapsedElement = /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("button", {
          ref: collapsedSetRef,
          type: "button",
          "aria-label": heading,
          "data-testid": testId,
          ...popover.triggerProps,
          ...triggerProps,
          ...mergeProps(themeProps('side-nav-heading'), stylex.props(navItemStyles.item, styles.rootCollapsed, styles.menuTrigger, xstyle), className, style),
          children: collapsedIcon
        }), popover.render(/*#__PURE__*/_jsxs("div", {
          ref: menuRef,
          role: "menu",
          ...{
            className: "x9epnlk xb3r6kr"
          },
          ...contentProps,
          children: [/*#__PURE__*/_jsxs("button", {
            type: "button",
            ...{
              className: "x78zum5 x6s0dn4 x1txdalj xh8yej3 xjbqb8w xjb2p0i x1qlqyl8 x1heor9g x1yc453h xkoleio x12gdq22 x1betex2 x1djylfy xt970qd xcsaf9d x1p37lm5 xhrcg97 x1ypdohk"
            },
            onClick: triggerProps.onClick,
            children: [icon && /*#__PURE__*/_jsx("span", {
              ...{
                className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
              },
              children: icon
            }), /*#__PURE__*/_jsxs("span", {
              ...{
                className: "x78zum5 xdt5ytf x98rzlu xeuugli"
              },
              children: [superheading && /*#__PURE__*/_jsx("span", {
                ...{
                  className: "x141an7d x1ltkj2j xv1l7n4 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
                },
                children: superheading
              }), /*#__PURE__*/_jsxs("span", {
                ...{
                  className: "x78zum5 x6s0dn4 xzye2dw"
                },
                children: [/*#__PURE__*/_jsx("span", {
                  ...{
                    0: {
                      className: "x18juvz8 x2mo6ok xf74fhv x1tgivj0 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
                    },
                    1: {
                      className: "x18juvz8 xf74fhv x1tgivj0 x1hl2dhg xb3r6kr xlyipyv xuxw1ft x2mo6ok"
                    }
                  }[!!!!(superheading || subheading) << 0],
                  children: heading
                }), /*#__PURE__*/_jsx("span", {
                  ...{
                    className: "x2lah0s x78zum5 x6s0dn4 xl56j7k xy8csz5 ximsjs8 xv9yike x19jd1h0"
                  },
                  children: getIcon('chevronDown')
                })]
              }), subheading && /*#__PURE__*/_jsx("span", {
                ...{
                  className: "x141an7d x1ltkj2j xv1l7n4 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
                },
                children: subheading
              })]
            })]
          }), /*#__PURE__*/_jsx(NavHeadingCloseContext, {
            value: closeMenuCtx,
            children: menu
          })]
        }), {
          placement: 'below',
          alignment: 'start',
          xstyle: styles.popover
        })]
      });
    } else {
      collapsedElement = /*#__PURE__*/_jsx("div", {
        ref: collapsedSetRef,
        "data-testid": testId,
        ...mergeProps(themeProps('side-nav-heading'), stylex.props(styles.root, styles.rootCollapsed, xstyle), className, style),
        ...props,
        children: collapsedIcon
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [collapsedElement, /*#__PURE__*/_jsx(Tooltip, {
        content: heading,
        placement: "end",
        anchorRef: collapsedItemRef
      })]
    });
  }
  const showChevron = !!menu;
  const hasAnyHref = !!(headingHref || superheadingHref || subheadingHref);
  const hasCompactHeading = !!(superheading || subheading);

  // Determine interaction mode
  const isWholeHeadingTrigger = !!menu && !hasAnyHref;
  const isWholeHeadingLink = !!headingHref && !menu && !superheadingHref && !subheadingHref;

  // Render text content with optional inline chevron
  const renderTextContent = inlineChevron => /*#__PURE__*/_jsxs("span", {
    ...{
      className: "x78zum5 xdt5ytf x98rzlu xeuugli"
    },
    children: [superheading && (hasAnyHref && superheadingHref && menu ? /*#__PURE__*/_jsx(Link, {
      href: superheadingHref,
      color: "secondary",
      size: "xsm",
      children: superheading
    }) : /*#__PURE__*/_jsx("span", {
      ...{
        className: "x141an7d x1ltkj2j xv1l7n4 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
      },
      children: superheading
    })), /*#__PURE__*/_jsxs("span", {
      ...{
        className: "x78zum5 x6s0dn4 xzye2dw"
      },
      children: [hasAnyHref && headingHref && menu ? /*#__PURE__*/_jsx(LinkComponent, {
        href: headingHref,
        ...{
          className: "x18juvz8 x2mo6ok xf74fhv x1hl2dhg x1heor9g xb3r6kr xlyipyv xuxw1ft"
        },
        children: heading
      }) : /*#__PURE__*/_jsx("span", {
        ...{
          className: "x18juvz8 x2mo6ok xf74fhv x1tgivj0 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
        },
        children: heading
      }), inlineChevron]
    }), subheading && (hasAnyHref && subheadingHref && menu ? /*#__PURE__*/_jsx(Link, {
      href: subheadingHref,
      color: "secondary",
      size: "xsm",
      children: subheading
    }) : /*#__PURE__*/_jsx("span", {
      ...{
        className: "x141an7d x1ltkj2j xv1l7n4 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
      },
      children: subheading
    }))]
  });
  const chevronElement = showChevron && /*#__PURE__*/_jsx("span", {
    ...{
      className: "x2lah0s x78zum5 x6s0dn4 xl56j7k xy8csz5 ximsjs8 xv9yike"
    },
    children: getIcon('chevronDown')
  });
  const headerEndContentElement = headerEndContent && /*#__PURE__*/_jsx("span", {
    ...{
      className: "x2lah0s x78zum5 x6s0dn4 xvc5jky"
    },
    children: headerEndContent
  });

  // Shared popover heading content — uses renderTextContent for consistent
  // sizing, with flipped chevron inline after the title. Always static (no links).
  const popoverHeadingContent = /*#__PURE__*/_jsxs("button", {
    type: "button",
    ...{
      className: "x78zum5 x6s0dn4 x1txdalj xh8yej3 xjbqb8w xjb2p0i x1qlqyl8 x1heor9g x1yc453h xkoleio x12gdq22 x1betex2 x1djylfy xt970qd xcsaf9d x1p37lm5 xhrcg97 x1ypdohk"
    },
    onClick: triggerProps.onClick,
    children: [icon && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
      },
      children: icon
    }), renderTextContent(/*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4 xl56j7k xy8csz5 ximsjs8 xv9yike x19jd1h0"
      },
      children: getIcon('chevronDown')
    }))]
  });

  // Whole heading is a link (no menu, single headingHref)
  if (isWholeHeadingLink && headingHref) {
    return /*#__PURE__*/_jsxs(LinkComponent, {
      ref: ref,
      href: headingHref,
      "data-testid": testId,
      ...mergeProps(themeProps('side-nav-heading'), stylex.props(styles.root, styles.menuTrigger, xstyle), className, style),
      ...props,
      children: [icon && /*#__PURE__*/_jsx("span", {
        ...{
          className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
        },
        children: icon
      }), renderTextContent(), headerEndContentElement, chevronElement]
    });
  }

  // Whole header is the popover trigger (menu, no hrefs)
  if (isWholeHeadingTrigger) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        ref: setRef,
        "data-testid": testId,
        ...triggerProps,
        ...mergeProps(themeProps('side-nav-heading'), stylex.props(styles.root, styles.menuTrigger, xstyle), className, style),
        children: [icon && /*#__PURE__*/_jsx("span", {
          ...{
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
          },
          children: icon
        }), renderTextContent(/*#__PURE__*/_jsx("button", {
          type: "button",
          "aria-label": t('@astryx.sideNav.heading.openMenu'),
          onClick: e => {
            e.stopPropagation();
            triggerProps.onClick();
          },
          ...popover.triggerProps,
          ...{
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k xy8csz5 ximsjs8 xv9yike x1ypdohk xh6dtrn xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x1sodnla x1yc453h xe9uy6x"
          },
          children: getIcon('chevronDown')
        })), headerEndContentElement]
      }), popover.render(/*#__PURE__*/_jsxs("div", {
        ref: menuRef,
        role: "menu",
        ...{
          className: "x9epnlk xb3r6kr"
        },
        ...contentProps,
        children: [popoverHeadingContent, menu]
      }), {
        placement: 'below',
        alignment: 'start',
        xstyle: styles.popoverOverlap
      })]
    });
  }

  // Mixed mode: independent links + chevron trigger for menu
  // Popover anchors to the full heading div, not the chevron, so it
  // appears in the same position as the no-links case.
  if (menu && hasAnyHref) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        ref: setRef,
        "data-testid": testId,
        ...triggerProps,
        ...mergeProps(themeProps('side-nav-heading'), stylex.props(styles.root, xstyle), className, style),
        children: [icon && (headingHref ? /*#__PURE__*/_jsx(LinkComponent, {
          href: headingHref,
          "aria-label": heading,
          ...{
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
          },
          children: icon
        }) : /*#__PURE__*/_jsx("span", {
          ...{
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
          },
          children: icon
        })), renderTextContent(showChevron ? /*#__PURE__*/_jsx("button", {
          type: "button",
          "aria-label": t('@astryx.sideNav.heading.openMenu'),
          onClick: e => {
            e.stopPropagation();
            triggerProps.onClick();
          },
          ...popover.triggerProps,
          ...{
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k xy8csz5 ximsjs8 xv9yike x1ypdohk xh6dtrn xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x1sodnla x1yc453h xe9uy6x"
          },
          children: getIcon('chevronDown')
        }) : undefined), headerEndContentElement]
      }), popover.render(/*#__PURE__*/_jsxs("div", {
        ref: menuRef,
        role: "menu",
        ...{
          className: "x9epnlk xb3r6kr"
        },
        ...contentProps,
        children: [popoverHeadingContent, menu]
      }), {
        placement: 'below',
        alignment: 'start',
        xstyle: styles.popoverOverlap
      })]
    });
  }

  // Static heading with independent links (no menu)
  if (hasAnyHref && !isWholeHeadingLink) {
    return /*#__PURE__*/_jsxs("div", {
      ref: ref,
      "data-testid": testId,
      ...mergeProps(themeProps('side-nav-heading'), stylex.props(styles.root, xstyle), className, style),
      ...props,
      children: [icon && (headingHref ? /*#__PURE__*/_jsx(LinkComponent, {
        href: headingHref,
        "aria-label": heading,
        ...{
          className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
        },
        children: icon
      }) : /*#__PURE__*/_jsx("span", {
        ...{
          className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
        },
        children: icon
      })), /*#__PURE__*/_jsxs("span", {
        ...{
          className: "x78zum5 xdt5ytf x98rzlu xeuugli"
        },
        children: [superheading && (superheadingHref ? /*#__PURE__*/_jsx(Link, {
          href: superheadingHref,
          color: "secondary",
          size: "xsm",
          children: superheading
        }) : /*#__PURE__*/_jsx("span", {
          ...{
            className: "x141an7d x1ltkj2j xv1l7n4 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
          },
          children: superheading
        })), headingHref ? /*#__PURE__*/_jsx(Link, {
          href: headingHref,
          color: "primary",
          weight: "semibold",
          children: heading
        }) : /*#__PURE__*/_jsx("span", {
          ...{
            0: {
              className: "x18juvz8 x2mo6ok xf74fhv x1tgivj0 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
            },
            1: {
              className: "x18juvz8 xf74fhv x1tgivj0 x1hl2dhg xb3r6kr xlyipyv xuxw1ft x2mo6ok"
            }
          }[!!hasCompactHeading << 0],
          children: heading
        }), subheading && (subheadingHref ? /*#__PURE__*/_jsx(Link, {
          href: subheadingHref,
          color: "secondary",
          size: "xsm",
          children: subheading
        }) : /*#__PURE__*/_jsx("span", {
          ...{
            className: "x141an7d x1ltkj2j xv1l7n4 x1hl2dhg xb3r6kr xlyipyv xuxw1ft"
          },
          children: subheading
        }))]
      }), headerEndContentElement, chevronElement]
    });
  }

  // Default: static heading, no links, no menu
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    "data-testid": testId,
    ...mergeProps(themeProps('side-nav-heading'), stylex.props(styles.root, xstyle), className, style),
    ...props,
    children: [icon && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x2lah0s x78zum5 x6s0dn4 xl56j7k"
      },
      children: icon
    }), renderTextContent(), headerEndContentElement, chevronElement]
  });
}
SideNavHeading.displayName = 'SideNavHeading';